import React from 'react';
import styled from 'styled-components';
import { AlertCircle, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { captureException } from '../../utils/errorTracking';

const ErrorContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.theme?.colors?.background || '#ffffff'};
  padding: 2rem;
`;

const ErrorContent = styled.div`
  text-align: center;
  max-width: 32rem;
`;

const ErrorIcon = styled.div`
  width: 4rem;
  height: 4rem;
  margin: 0 auto 1.5rem;
  color: ${props => props.theme?.colors?.error || '#ef4444'};
  
  @media (min-width: 1024px) {
    width: 5rem;
    height: 5rem;
    margin-bottom: 2rem;
  }
  
  svg {
    width: 100%;
    height: 100%;
  }
`;

const ErrorTitle = styled.h1`
  font-family: ${props => props.theme?.typography?.fonts?.display || 'system-ui, sans-serif'};
  font-size: 1.75rem;
  font-weight: ${props => props.theme?.typography?.fontWeights?.semibold || 600};
  margin-bottom: 0.75rem;
  
  @media (min-width: 768px) {
    font-size: 2rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }
`;

const ErrorMessage = styled.p`
  font-size: 1rem;
  color: ${props => props.theme?.colors?.mutedForeground || '#6b7280'};
  margin-bottom: 2rem;
  line-height: 1.6;
  
  @media (min-width: 1024px) {
    font-size: 1.125rem;
    margin-bottom: 2.5rem;
  }
`;

const ErrorDetails = styled.details`
  text-align: left;
  margin-top: 2rem;
  padding: 1rem;
  background-color: ${props => props.theme?.colors?.muted || '#f3f4f6'};
  border-radius: ${props => props.theme?.borderRadius?.lg || '0.5rem'};
  font-size: 0.875rem;
  color: ${props => props.theme?.colors?.mutedForeground || '#6b7280'};
  
  summary {
    cursor: pointer;
    font-weight: 500;
    margin-bottom: 0.5rem;
    color: ${props => props.theme?.colors?.foreground || '#111827'};
  }
  
  pre {
    margin-top: 0.5rem;
    overflow-x: auto;
    font-size: 0.75rem;
  }
`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Report to error tracking service
    captureException(error, {
      tags: {
        component: 'ErrorBoundary',
        errorBoundary: true
      },
      extra: {
        componentStack: errorInfo.componentStack
      }
    });
    
    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback 
          error={this.state.error} 
          errorInfo={this.state.errorInfo} 
        />
      );
    }

    return this.props.children;
  }
}

// Functional component wrapper to use hooks
const ErrorFallback = ({ error, errorInfo }) => {
  const { t } = useTranslation();
  
  return (
    <ErrorContainer>
      <ErrorContent>
        <ErrorIcon>
          <AlertCircle />
        </ErrorIcon>
        <ErrorTitle>{t('error.boundary.title')}</ErrorTitle>
        <ErrorMessage>
          {t('error.boundary.message')}
        </ErrorMessage>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="primary"
            onClick={() => window.location.href = '/'}
          >
            <Home />
            {t('error.boundary.home')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => window.location.reload()}
          >
            {t('error.boundary.refresh')}
          </Button>
        </div>
        {process.env.NODE_ENV === 'development' && error && (
          <ErrorDetails>
            <summary>Error Details (Development Only)</summary>
            <pre>{error.toString()}</pre>
            {errorInfo && (
              <pre>{errorInfo.componentStack}</pre>
            )}
          </ErrorDetails>
        )}
      </ErrorContent>
    </ErrorContainer>
  );
};

export default ErrorBoundary;



